import { useListStringsLazyQuery } from 'src/generated/graphql';
import { MAX_SNAPSHOT_PAGES, SNAPSHOT_PAGE_SIZE } from '../constants';
import { StringInfo, TranslationStatus } from '../types';
import { SnapshotError } from './snapshotError';

/** The imperative fetch function returned by `useListStringsLazyQuery`. */
type ListStringsFetch = ReturnType<typeof useListStringsLazyQuery>[0];

/**
 * Walks every ListStrings page for a single version into a flat `StringInfo[]`,
 * exhausting pagination before returning.
 *
 * The list is returned in page order WITHOUT de-duplicating or merging by
 * stringId — that post-processing is intentionally left to the caller, because
 * the two consumers need opposite behaviour on a repeated stringId:
 *   - DiffModal unions variants across pages (a string may legitimately span a
 *     page boundary).
 *   - RollbackModal treats a repeat as corruption and fails fast via
 *     `computeRollbackDelta`'s DuplicateStringIdError, rather than submit a
 *     delta computed from a silently-merged snapshot.
 *
 * Guards against a backend that never terminates pagination — a repeated
 * nextToken or an unbounded page count fails fast instead of hanging the UI.
 * The page size is pinned so MAX_SNAPSHOT_PAGES bounds the same number of
 * strings for every reader.
 *
 * When `version` is omitted the latest version is served (no
 * `stringGroupVersion` is sent) — this lets the strings-table hook share this
 * walk while still defaulting to latest.
 *
 * Guard failures throw a {@link SnapshotError} carrying a structured `code` and
 * an English diagnostic message. The code, not the message, is what the callers
 * render copy from: this module has no `t` (and taking one would put `t` in
 * every caller's fetch-effect dependency list, re-fetching the whole snapshot
 * from the network on a display-language change).
 */
export async function fetchVersionSnapshot(
  fetchFn: ListStringsFetch,
  namespaceId: string,
  stringGroupId: string,
  version?: number,
): Promise<StringInfo[]> {
  const allStrings: StringInfo[] = [];
  const seenPageTokens = new Set<string>();
  let pageCount = 0;
  let nextToken: string | null = null;

  do {
    pageCount += 1;
    if (pageCount > MAX_SNAPSHOT_PAGES) {
      throw new SnapshotError(
        'page-limit',
        `Snapshot pagination exceeded ${MAX_SNAPSHOT_PAGES} pages`,
      );
    }

    const { data, error } = await fetchFn({
      fetchPolicy: 'network-only',
      variables: {
        namespaceId,
        stringGroupId,
        ...(version != null ? { stringGroupVersion: version.toString() } : {}),
        maxResults: SNAPSHOT_PAGE_SIZE,
        ...(nextToken ? { nextToken } : {}),
      },
    });

    if (error) {
      throw error;
    }

    // A page that resolves without data must fail loudly. Skipping it would
    // yield an empty (or partial) snapshot, which a delta computed downstream
    // would misread as "every string was deleted" — a destructive rollback.
    if (!data) {
      throw new SnapshotError(
        'no-data',
        'ListStrings page resolved without data',
      );
    }

    for (const stringInfo of data.listStrings.strings) {
      allStrings.push({
        stringId: stringInfo.stringId,
        sourceLocale: stringInfo.sourceLocale,
        sourceText: stringInfo.sourceText,
        contextNote: stringInfo.contextNote ?? undefined,
        inlineMarkupType: stringInfo.inlineMarkupType ?? undefined,
        messageFormatType: stringInfo.messageFormatType ?? undefined,
        clientId: 'unknown',
        variants: stringInfo.variants.map((variant) => ({
          cxId: variant.cxId,
          sourceText: variant.sourceText,
          contextNote: variant.contextNote ?? undefined,
        })),
        translationStatus:
          (stringInfo.status as TranslationStatus | null) ??
          TranslationStatus.Complete,
      });
    }

    const returnedNextToken = data.listStrings.nextToken ?? null;
    // Check membership before adding — Set.add() returns the Set (always
    // truthy), so it cannot double as the "already seen" signal.
    if (returnedNextToken && seenPageTokens.has(returnedNextToken)) {
      throw new SnapshotError(
        'repeated-token',
        'ListStrings returned a nextToken it had already issued',
      );
    }
    if (returnedNextToken) {
      seenPageTokens.add(returnedNextToken);
    }
    nextToken = returnedNextToken;
  } while (nextToken);

  return allStrings;
}
